import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  CheckCircle2,
  Circle,
  Clock,
  MinusCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  Eye,
  ExternalLink,
  Loader2,
  BookOpen,
  ClipboardList,
  PenLine,
} from "lucide-react";
import { useTRPC, useTRPCClient } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  HiringProcessAssigneeRole,
  HiringProcessItemStatus,
  HiringProcessItemType,
} from "@api/shared-runtime/enums";
import type { RouterOutputs } from "@/trpc";
import { toast } from "sonner";
import { FormSubmissionDialog } from "./form-submission-dialog";
import { FormResponseDialog } from "./form-response-dialog";
import { ChatAttachmentPreviewModal } from "@/features/chat/ChatAttachmentPreviewModal";
import { ProviderDocumentUpload } from "./provider-document-upload";
import { PdfSignDialog } from "./pdf/pdf-sign-dialog";
import { SharedResourceList } from "./shared-resource-list";

type HiringProcessInstance = NonNullable<
  RouterOutputs["hiringProcess"]["getMyProcess"]
>;

function statusIcon(status: HiringProcessItemStatus) {
  switch (status) {
    case HiringProcessItemStatus.Completed:
      return <CheckCircle2 size={18} className="text-green-500 shrink-0" />;
    case HiringProcessItemStatus.InProgress:
      return <Clock size={18} className="text-blue-500 shrink-0" />;
    case HiringProcessItemStatus.Skipped:
      return <MinusCircle size={18} className="text-gray-400 shrink-0" />;
    case HiringProcessItemStatus.NotApplicable:
      return <XCircle size={18} className="text-gray-300 shrink-0" />;
    default:
      return <Circle size={18} className="text-gray-300 shrink-0" />;
  }
}

interface ProviderItemRowProps {
  item: HiringProcessInstance["items"][number];
  onStatusChange: (id: string, status: HiringProcessItemStatus) => void;
  isUpdating: boolean;
  jobPostId: string;
  /** The candidate (this process's subject) — used to sign file forms. */
  candidateUserId: string;
  onAttachmentsChanged: () => void;
}

function ProviderItemRow({
  item,
  onStatusChange,
  isUpdating,
  jobPostId,
  candidateUserId,
  onAttachmentsChanged,
}: ProviderItemRowProps) {
  const trpcClient = useTRPCClient();
  const isFormItem = item.itemType === HiringProcessItemType.Form;
  const isDocShare = item.itemType === HiringProcessItemType.DocumentShare;
  const isSigningItem = item.itemType === HiringProcessItemType.DocumentSigning;
  const isCompleted = item.status === HiringProcessItemStatus.Completed;
  const sharedFiles = [
    ...(isDocShare ? (item.fileAttachments ?? []) : []),
    ...(item.sharedFromPredecessors?.files ?? []),
  ].sort((a, b) => a.sortOrder - b.sortOrder);
  const sharedLinks = [
    ...(isDocShare ? (item.links ?? []) : []),
    ...(item.sharedFromPredecessors?.links ?? []),
  ].sort((a, b) => a.sortOrder - b.sortOrder);
  const primarySharedFile = sharedFiles.find(
    (attachment) => attachment.fileUpload.scanStatus === "clean",
  );
  const primarySharedFilePreviewable =
    !!primarySharedFile &&
    (primarySharedFile.fileUpload.mimeType === "application/pdf" ||
      primarySharedFile.fileUpload.mimeType.startsWith("image/"));

  const [formSubmitOpen, setFormSubmitOpen] = useState(false);
  const [formResponseOpen, setFormResponseOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [confirmedRead, setConfirmedRead] = useState(false);
  const [inFlightId, setInFlightId] = useState<string | null>(null);
  const [inFlightAction, setInFlightAction] = useState<
    "download" | "preview" | null
  >(null);
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    fileName: string;
    mimeType: string;
  } | null>(null);

  const handleFileDownload = async (
    fileUploadId: string,
    hiringProcessItemId: string,
  ) => {
    if (inFlightId) {
      return;
    }
    setInFlightId(fileUploadId);
    setInFlightAction("download");
    try {
      const result =
        await trpcClient.hiringProcess.getItemFileDownloadUrl.query({
          fileUploadId,
          jobPostId,
          hiringProcessItemId,
        });
      window.open(result.signedUrl, "_blank");
    } catch {
      // silently fail — URL fetch errors are non-critical
    } finally {
      setInFlightId(null);
      setInFlightAction(null);
    }
  };

  const handleFilePreview = async (
    fileUploadId: string,
    hiringProcessItemId: string,
    fileName: string,
    mimeType: string,
  ) => {
    if (inFlightId) {
      return;
    }
    setInFlightId(fileUploadId);
    setInFlightAction("preview");
    try {
      const result =
        await trpcClient.hiringProcess.getItemFileDownloadUrl.query({
          fileUploadId,
          jobPostId,
          hiringProcessItemId,
        });
      setPreviewFile({ url: result.signedUrl, fileName, mimeType });
    } catch {
      // silently fail — URL fetch errors are non-critical
    } finally {
      setInFlightId(null);
      setInFlightAction(null);
    }
  };

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
      {statusIcon(item.status as HiringProcessItemStatus)}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className={`text-sm font-medium ${
                  isCompleted ? "text-gray-400 line-through" : "text-gray-800"
                }`}
              >
                {item.title}
              </span>
              {!item.isRequired && (
                <span className="text-xs text-gray-400 font-normal">
                  (optional)
                </span>
              )}
              {isFormItem && (
                <Badge
                  variant="outline"
                  className="text-xs bg-purple-50 text-purple-700 border-purple-200 h-4 px-1.5"
                >
                  <FileText size={9} className="mr-1" />
                  Form
                </Badge>
              )}
              {isDocShare && (
                <Badge
                  variant="outline"
                  className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200 h-4 px-1.5"
                >
                  <BookOpen size={9} className="mr-1" />
                  Shared
                </Badge>
              )}
              {isSigningItem && (
                <Badge
                  variant="outline"
                  className="text-xs bg-green-50 text-green-700 border-green-200 h-4 px-1.5"
                >
                  <PenLine size={9} className="mr-1" />
                  PDF
                </Badge>
              )}
            </div>
            {item.description && (
              <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
            )}

            {/* Rich text content for DocShare */}
            {isDocShare && item.richTextContent && (
              <div className="mt-1.5">
                <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-700 whitespace-pre-wrap border border-gray-100">
                  {item.richTextContent}
                </div>
              </div>
            )}

            {/*
             * Files and links to show on this item.
             *   - DocShare items: the item's own template attachments/links.
             *   - Any item type: content passed through from predecessors
             *     with shareContent=true (e.g., an org DocumentUpload that
             *     feeds a provider acknowledgement task). The backend only
             *     emits predecessor content when shareContent is true — do
             *     NOT add client-side gating here.
             */}
            {(() => {
              const allFiles = sharedFiles;
              const allLinks = sharedLinks;
              if (allFiles.length === 0 && allLinks.length === 0) {
                return null;
              }
              return (
                <SharedResourceList
                  className="mt-3"
                  files={allFiles}
                  links={allLinks}
                  inFlightId={inFlightId}
                  inFlightAction={inFlightAction}
                  onPreviewFile={(attachment) =>
                    handleFilePreview(
                      attachment.fileUpload.id,
                      item.hiringProcessItemId,
                      attachment.displayName,
                      attachment.fileUpload.mimeType,
                    )
                  }
                  onDownloadFile={(attachment) =>
                    handleFileDownload(
                      attachment.fileUpload.id,
                      item.hiringProcessItemId,
                    )
                  }
                />
              );
            })()}

            {isDocShare &&
              !isCompleted &&
              (item.richTextContent ||
                sharedFiles.length > 0 ||
                sharedLinks.length > 0) && (
                <div className="flex items-center gap-2 mt-3">
                  <Checkbox
                    id={`confirm-read-${item.id}`}
                    checked={confirmedRead}
                    disabled={isUpdating || confirmedRead}
                    onCheckedChange={(checked) => {
                      if (checked === true) {
                        setConfirmedRead(true);
                        onStatusChange(
                          item.id,
                          HiringProcessItemStatus.Completed,
                        );
                      }
                    }}
                  />
                  <Label
                    htmlFor={`confirm-read-${item.id}`}
                    className="text-xs text-gray-600 cursor-pointer"
                  >
                    I confirm I have read this document
                  </Label>
                </div>
              )}
          </div>

          <div className="shrink-0">
            {isFormItem ? (
              isCompleted ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-600 font-medium">
                    Completed
                  </span>
                  {item.formSubmissionId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
                      onClick={() => setFormResponseOpen(true)}
                    >
                      <Eye size={12} className="mr-1" />
                      View
                    </Button>
                  )}
                </div>
              ) : (
                <Button
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={() => setFormSubmitOpen(true)}
                >
                  <FileText size={12} className="mr-1.5" />
                  Fill Out Form
                </Button>
              )
            ) : isSigningItem ? (
              isCompleted ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-600 font-medium">
                    Signed
                  </span>
                  {item.formSubmissionId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
                      onClick={() => setFormResponseOpen(true)}
                    >
                      <Eye size={12} className="mr-1" />
                      View
                    </Button>
                  )}
                </div>
              ) : !item.fileFormId ? (
                <span className="text-xs text-gray-400">Awaiting document</span>
              ) : (
                <Button
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={() => setSignOpen(true)}
                >
                  <PenLine size={12} className="mr-1.5" />
                  Sign Document
                </Button>
              )
            ) : isDocShare ? (
              isCompleted ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-600 font-medium">
                    Reviewed
                  </span>
                  {primarySharedFile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
                      onClick={() =>
                        primarySharedFilePreviewable
                          ? handleFilePreview(
                              primarySharedFile.fileUpload.id,
                              item.hiringProcessItemId,
                              primarySharedFile.displayName,
                              primarySharedFile.fileUpload.mimeType,
                            )
                          : handleFileDownload(
                              primarySharedFile.fileUpload.id,
                              item.hiringProcessItemId,
                            )
                      }
                      disabled={!!inFlightId}
                    >
                      <Eye size={12} className="mr-1" />
                      {primarySharedFilePreviewable ? "View" : "Download"}
                    </Button>
                  )}
                  {!primarySharedFile && sharedLinks[0] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-blue-600 hover:text-blue-700"
                      onClick={() =>
                        window.open(sharedLinks[0].url, "_blank", "noreferrer")
                      }
                    >
                      <ExternalLink size={12} className="mr-1" />
                      Open
                    </Button>
                  )}
                </div>
              ) : primarySharedFile ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-3 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() =>
                    primarySharedFilePreviewable
                      ? handleFilePreview(
                          primarySharedFile.fileUpload.id,
                          item.hiringProcessItemId,
                          primarySharedFile.displayName,
                          primarySharedFile.fileUpload.mimeType,
                        )
                      : handleFileDownload(
                          primarySharedFile.fileUpload.id,
                          item.hiringProcessItemId,
                        )
                  }
                  disabled={isUpdating || !!inFlightId}
                >
                  <Eye size={12} className="mr-1.5" />
                  {primarySharedFilePreviewable
                    ? "View Document"
                    : "Download Document"}
                </Button>
              ) : sharedLinks[0] ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-3 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() =>
                    window.open(sharedLinks[0].url, "_blank", "noreferrer")
                  }
                  disabled={isUpdating}
                >
                  <ExternalLink size={12} className="mr-1.5" />
                  Open Link
                </Button>
              ) : !item.richTextContent &&
                sharedFiles.length === 0 &&
                sharedLinks.length === 0 ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-3 text-xs"
                  onClick={() =>
                    onStatusChange(item.id, HiringProcessItemStatus.Completed)
                  }
                  disabled={isUpdating}
                >
                  <Eye size={12} className="mr-1.5" />
                  Mark as Reviewed
                </Button>
              ) : null
            ) : (
              <Select
                value={item.status}
                onValueChange={(value) =>
                  onStatusChange(item.id, value as HiringProcessItemStatus)
                }
                disabled={isUpdating}
              >
                <SelectTrigger className="h-8 w-36 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value={HiringProcessItemStatus.NotStarted}
                    className="text-xs"
                  >
                    Not Started
                  </SelectItem>
                  <SelectItem
                    value={HiringProcessItemStatus.InProgress}
                    className="text-xs"
                  >
                    In Progress
                  </SelectItem>
                  <SelectItem
                    value={HiringProcessItemStatus.Completed}
                    className="text-xs"
                  >
                    Completed
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {item.itemType === HiringProcessItemType.DocumentUpload &&
        item.assigneeRole === HiringProcessAssigneeRole.Provider && (
          <ProviderDocumentUpload
            hiringProcessItemProgressId={item.hiringProcessItemProgressId}
            hiringProcessItemId={item.hiringProcessItemId}
            jobPostId={jobPostId}
            requestedCredentialFileTypeId={item.requestedCredentialFileTypeId}
            existingAttachments={item.providerAttachments ?? []}
            onAttachmentsChanged={onAttachmentsChanged}
            status={item.status}
            onMarkComplete={() =>
              onStatusChange(
                item.hiringProcessItemId,
                HiringProcessItemStatus.Completed,
              )
            }
            onReopen={() =>
              onStatusChange(
                item.hiringProcessItemId,
                HiringProcessItemStatus.InProgress,
              )
            }
            isUpdatingStatus={isUpdating}
          />
        )}

      {isFormItem && item.formId && (
        <FormSubmissionDialog
          open={formSubmitOpen}
          onOpenChange={setFormSubmitOpen}
          formId={item.formId}
          anchor={{
            kind: "hiring",
            jobPostId,
            hiringProcessItemId: item.hiringProcessItemId,
          }}
          suggestionsEnabled
        />
      )}

      {(isFormItem || isSigningItem) && item.formSubmissionId && (
        <FormResponseDialog
          open={formResponseOpen}
          onOpenChange={setFormResponseOpen}
          formSubmissionId={item.formSubmissionId}
        />
      )}

      {isSigningItem && item.fileFormId && (
        <PdfSignDialog
          open={signOpen}
          onOpenChange={setSignOpen}
          formId={item.fileFormId}
          anchor={{
            kind: "hiring",
            jobPostId,
            hiringProcessItemId: item.hiringProcessItemId,
            candidateUserId,
          }}
          title={item.title}
          onSigned={() => onAttachmentsChanged()}
        />
      )}

      {previewFile && (
        <ChatAttachmentPreviewModal
          url={previewFile.url}
          fileName={previewFile.fileName}
          mimeType={previewFile.mimeType}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
}

interface ProviderHiringProcessViewProps {
  jobPostId: string;
}

export function ProviderHiringProcessView({
  jobPostId,
}: ProviderHiringProcessViewProps) {
  const trpc = useTRPC();
  const [isExpanded, setIsExpanded] = useState(true);
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);

  const { data: instance, isLoading } = useQuery(
    trpc.hiringProcess.getMyProcess.queryOptions({ jobPostId }),
  );

  const updateStatusMutation = useMutation(
    trpc.hiringProcess.updateItemStatus.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.hiringProcess.getMyProcess.queryOptions({ jobPostId }),
        );
        await queryClient.invalidateQueries(
          trpc.hiringProcess.getMyProcesses.queryOptions(),
        );
        setUpdatingItemId(null);
        toast.success("Item updated");
      },
      onError: (err) => {
        setUpdatingItemId(null);
        const message =
          err instanceof Error ? err.message : "Failed to update item";
        toast.error(message);
      },
    }),
  );

  const handleStatusChange = (
    hiringProcessItemId: string,
    status: HiringProcessItemStatus,
  ) => {
    if (!instance) {
      return;
    }
    setUpdatingItemId(hiringProcessItemId);
    updateStatusMutation.mutate({
      jobPostId,
      userId: instance.userId,
      hiringProcessItemId,
      status,
    });
  };

  const handleAttachmentsChanged = async () => {
    await Promise.all([
      queryClient.invalidateQueries(
        trpc.hiringProcess.getMyProcess.queryOptions({ jobPostId }),
      ),
      queryClient.invalidateQueries(
        trpc.hiringProcess.getMyProcesses.queryOptions(),
      ),
    ]);
  };

  if (isLoading) {
    return (
      <div className="border border-gray-100 rounded-xl p-4 flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 text-blue-600 animate-spin mr-2" />
        <span className="text-gray-500 text-sm">Loading...</span>
      </div>
    );
  }

  if (!instance || instance.items.length === 0) {
    return null;
  }

  const items = instance.items;
  const pendingCount = items.filter(
    (i) =>
      i.isRequired &&
      i.status !== HiringProcessItemStatus.Completed &&
      i.status !== HiringProcessItemStatus.Skipped &&
      i.status !== HiringProcessItemStatus.NotApplicable,
  ).length;
  const totalRequired = items.filter((i) => i.isRequired).length;
  const _completedRequired = totalRequired - pendingCount;
  const allDone = pendingCount === 0;

  return (
    <div
      className={`rounded-xl overflow-hidden shadow-sm ${
        allDone
          ? "border border-green-200 bg-white"
          : "border-2 border-amber-300 bg-white ring-1 ring-amber-100"
      }`}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded((prev) => !prev)}
        className={`w-full flex items-center justify-between p-4 transition-colors text-left ${
          allDone
            ? "bg-green-50 hover:bg-green-100/70"
            : "bg-amber-50 hover:bg-amber-100/70"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-lg ${allDone ? "bg-green-100" : "bg-amber-100"}`}
          >
            <ClipboardList
              className={`h-5 w-5 shrink-0 ${allDone ? "text-green-600" : "text-amber-600"}`}
            />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-base">
              Required Application Items
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              {allDone ? (
                <Badge className="bg-green-100 text-green-700 border border-green-200">
                  <CheckCircle2 size={12} className="mr-1" />
                  All done
                </Badge>
              ) : (
                <span className="text-sm font-medium text-amber-700">
                  {pendingCount} of {totalRequired} items remaining
                </span>
              )}
            </div>
          </div>
        </div>
        {isExpanded ? (
          <ChevronDown size={16} className="text-gray-400 shrink-0" />
        ) : (
          <ChevronRight size={16} className="text-gray-400 shrink-0" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100 px-4">
          {items.map((item) => (
            <ProviderItemRow
              key={item.id}
              item={item}
              onStatusChange={handleStatusChange}
              isUpdating={
                updatingItemId === item.id && updateStatusMutation.isPending
              }
              jobPostId={jobPostId}
              candidateUserId={instance.userId}
              onAttachmentsChanged={handleAttachmentsChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}
